/**
 * Import History Page
 * Shows all CSV import runs for the current clinic, newest first.
 */

import React, { useState } from 'react';
import { History, Upload } from 'lucide-react';
import { useImportRuns } from '../hooks/useImportRuns';
import ImportRunCard from '../components/imports/ImportRunCard';
import UploadWizard from '../components/patients/UploadWizard';
import toast from 'react-hot-toast';

const ImportHistory = () => {
  const { runs, loading, error, refetch } = useImportRuns();
  const [isUploadWizardOpen, setIsUploadWizardOpen] = useState(false);

  const handleUploadComplete = (result) => {
    setIsUploadWizardOpen(false);
    if (result?.created > 0) {
      toast.success(`Imported ${result.created} patients`);
    }
    refetch();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-3xl mx-auto px-6 py-8 space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
              <History className="h-5 w-5 text-slate-400" strokeWidth={1.5} />
              Import History
            </h1>
            <p className="text-sm text-slate-500 mt-0.5">
              Every CSV upload with a snapshot of the rules used to evaluate patients
            </p>
          </div>
          <button
            onClick={() => setIsUploadWizardOpen(true)}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-slate-900 hover:bg-slate-800 rounded-lg transition-colors"
          >
            <Upload className="h-4 w-4" strokeWidth={1.5} />
            Upload Patient List
          </button>
        </div>

        {/* Loading */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-20 text-slate-400">
            <div className="w-6 h-6 border-2 border-slate-200 border-t-slate-500 rounded-full animate-spin mb-3" />
            <p className="text-xs">Loading imports...</p>
          </div>
        )}

        {/* Error */}
        {error && !loading && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">
            Failed to load import history. Please try again.
          </div>
        )}

        {/* Import runs list */}
        {!loading && !error && runs.length > 0 && (
          <div className="space-y-4">
            {runs.map((run) => (
              <ImportRunCard key={run.run_id} run={run} />
            ))}
          </div>
        )}

        {/* Empty state */}
        {!loading && !error && runs.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 bg-white rounded-lg border border-slate-200">
            <Upload className="h-8 w-8 text-slate-300 mb-3" strokeWidth={1.5} />
            <h3 className="text-sm font-medium text-slate-900">No imports yet</h3>
            <p className="text-xs text-slate-500 mt-1 mb-4">
              Upload a patient list to get started
            </p>
            <button
              onClick={() => setIsUploadWizardOpen(true)}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-slate-900 hover:bg-slate-800 rounded-lg transition-colors"
            >
              <Upload className="h-4 w-4" strokeWidth={1.5} />
              Upload Patient List
            </button>
          </div>
        )}
      </div>

      <UploadWizard
        isOpen={isUploadWizardOpen}
        onClose={() => setIsUploadWizardOpen(false)}
        onComplete={handleUploadComplete}
      />
    </div>
  );
};

export default ImportHistory;
